import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { normalizeError } from "../../lib/normalizeError";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { ScrollArea } from "../../components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../../components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import { Bell, Plus, Edit, Trash2, Send, RefreshCw, Mail, AlertTriangle, CheckCircle, Clock, XCircle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const EVENT_META = [
  { code: "BELOW_ROP", label: "Stock Below Reorder Point", desc: "Triggers when on-hand falls at/below reorder point." },
  { code: "REQUEST_CREATED", label: "Replenishment Requested", desc: "Triggers when the system creates a new order queue entry." },
  { code: "ORDER_APPROVED", label: "Order Approved", desc: "Triggers when a request is approved and moves forward." },
  { code: "ORDER_PLACED", label: "Order Placed", desc: "Triggers when the order is marked as placed with vendor/PO." },
  { code: "ORDER_RECEIVED", label: "Order Received (Complete)", desc: "Triggers when full quantity is received and inventory updated." },
  { code: "ORDER_PARTIALLY_RECEIVED", label: "Order Partially Received", desc: "Triggers when a partial delivery is received." },
  { code: "ORDER_DENIED", label: "Request Denied", desc: "Triggers when the request is rejected." },
  { code: "ORDER_CANCELLED", label: "Order Cancelled", desc: "Triggers when the request/order is cancelled." },
  { code: "TEST", label: "Test Email (Manual)", desc: "Manual test emails sent from admin." },
];

const EVENT_LABEL = Object.fromEntries(EVENT_META.map(e => [e.code, e.label]));
const MATCH_TYPES = ["DEFAULT", "ITEM", "LOCATION", "DEPARTMENT"];

export default function AdminNotifications() {
  const { getAuthHeader } = useAuth();
  const [rules, setRules] = useState([]);
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [formData, setFormData] = useState({
    match_type: "DEFAULT",
    match_id: "",
    to_emails: "",
    cc_emails: "",
    enabled: true,
    event_types: []
  });

  // Delivery Log state
  const [deliveryLog, setDeliveryLog] = useState([]);
  const [deliveryStats, setDeliveryStats] = useState(null);
  const [loadingDelivery, setLoadingDelivery] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [resending, setResending] = useState(null);

  // Clear Logs state
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearScope, setClearScope] = useState("all");
  const [clearOlderThan, setClearOlderThan] = useState("");
  const [clearConfirmText, setClearConfirmText] = useState("");
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    fetchData();
    fetchDeliveryLog();
  }, []);

  const fetchData = async () => {
    try {
      const [rulesRes, itemsRes, locsRes, deptsRes] = await Promise.all([
        axios.get(`${API}/notification-rules`, { withCredentials: true }),
        axios.get(`${API}/items`, { withCredentials: true }),
        axios.get(`${API}/locations`, { withCredentials: true }),
        axios.get(`${API}/departments`, { withCredentials: true })
      ]);
      setRules(rulesRes.data);
      setItems(itemsRes.data);
      setLocations(locsRes.data);
      setDepartments(deptsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchDeliveryLog = async () => {
    setLoadingDelivery(true);
    try {
      const [logRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/notification-outbox?limit=50`, { withCredentials: true }),
        axios.get(`${API}/admin/notification-outbox/stats`, { withCredentials: true })
      ]);
      setDeliveryLog(logRes.data);
      setDeliveryStats(statsRes.data);
    } catch (error) {
      console.error("Failed to load delivery log", error);
    } finally {
      setLoadingDelivery(false);
    }
  };

  const handleSendTest = async () => {
    if (!testEmail) {
      toast.error("Please enter an email address");
      return;
    }
    setSendingTest(true);
    try {
      const res = await axios.post(`${API}/admin/notification-test`, 
        { to_email: testEmail }, 
        { withCredentials: true }
      );
      if (res.data.success) {
        toast.success(`Test email sent via ${res.data.sent_via}`);
      } else {
        toast.error(`Failed: ${res.data.error}`);
      }
      setTestDialogOpen(false);
      setTestEmail("");
      fetchDeliveryLog();
    } catch (error) {
      toast.error(normalizeError(error.response?.data) || "Failed to send test");
    } finally {
      setSendingTest(false);
    }
  };

  const handleResend = async (entryId) => {
    setResending(entryId);
    try {
      const res = await axios.post(`${API}/admin/notification-outbox/${entryId}/resend`, 
        {}, 
        { withCredentials: true }
      );
      if (res.data.success) {
        toast.success(`Email resent via ${res.data.sent_via}`);
      } else {
        toast.error(`Failed: ${res.data.error}`);
      }
      fetchDeliveryLog();
    } catch (error) {
      toast.error(normalizeError(error.response?.data) || "Failed to resend");
    } finally {
      setResending(null);
    }
  };

  // Clear logs handler
  const handleClearLogs = useCallback(async () => {
    // Validate confirmation for "all" scope without age filter
    const needsStrongConfirm = clearScope === "all" && !clearOlderThan;
    if (needsStrongConfirm && clearConfirmText !== "CLEAR") {
      toast.error("Please type CLEAR to confirm deletion of all logs");
      return;
    }

    setClearing(true);
    try {
      const payload = {
        scope: clearScope,
        confirm_all: clearScope === "all" && !clearOlderThan
      };
      
      if (clearOlderThan && parseInt(clearOlderThan) > 0) {
        payload.older_than_days = parseInt(clearOlderThan);
      }

      const res = await axios.delete(`${API}/admin/notification-outbox/clear`, {
        headers: getAuthHeader(),
        data: payload
      });

      const count = res.data.deleted_count || 0;
      toast.success(`Cleared ${count} log${count !== 1 ? 's' : ''}`);
      
      // Reset and close
      setClearDialogOpen(false);
      setClearScope("all");
      setClearOlderThan("");
      setClearConfirmText("");
      
      // Refresh data
      fetchDeliveryLog();
    } catch (error) {
      toast.error(normalizeError(error.response?.data) || "Failed to clear logs");
    } finally {
      setClearing(false);
    }
  }, [clearScope, clearOlderThan, clearConfirmText, getAuthHeader]);

  // Check if clear button should be enabled
  const canClear = useCallback(() => {
    if (clearScope === "all" && !clearOlderThan) {
      return clearConfirmText === "CLEAR";
    }
    return true;
  }, [clearScope, clearOlderThan, clearConfirmText]);

  const getStatusBadge = (status, sentVia) => {
    switch (status) {
      case "SENT":
        return (
          <Badge className="industrial-badge bg-green-900/30 text-green-400 border-green-900">
            <CheckCircle className="w-3 h-3 mr-1" />
            {sentVia === "MOCK" ? "SENT (MOCK)" : "SENT"}
          </Badge>
        );
      case "FAILED":
        return (
          <Badge className="industrial-badge bg-red-900/30 text-red-400 border-red-900">
            <XCircle className="w-3 h-3 mr-1" />
            FAILED
          </Badge>
        );
      case "PENDING":
        return (
          <Badge className="industrial-badge bg-yellow-900/30 text-yellow-400 border-yellow-900">
            <Clock className="w-3 h-3 mr-1" />
            PENDING
          </Badge>
        );
      default:
        return (
          <Badge className="industrial-badge bg-muted text-muted-foreground border-border">
            {status}
          </Badge>
        );
    }
  };

  const resetForm = () => {
    setFormData({
      match_type: "DEFAULT",
      match_id: "",
      to_emails: "",
      cc_emails: "",
      enabled: true,
      event_types: []
    });
    setEditingRule(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      to_emails: formData.to_emails.split(",").map(e => e.trim()).filter(Boolean),
      cc_emails: formData.cc_emails.split(",").map(e => e.trim()).filter(Boolean),
      match_id: formData.match_type === "DEFAULT" ? null : formData.match_id
    };
    
    try {
      if (editingRule) {
        await axios.put(`${API}/notification-rules/${editingRule.id}`, payload, { withCredentials: true });
        toast.success("Rule updated");
      } else {
        await axios.post(`${API}/notification-rules`, payload, { withCredentials: true });
        toast.success("Rule created");
      }
      setSheetOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to save rule");
    }
  };

  const handleDelete = async (ruleId) => {
    if (!confirm("Delete this notification rule?")) return;
    
    try {
      await axios.delete(`${API}/notification-rules/${ruleId}`, { withCredentials: true });
      toast.success("Rule deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete rule");
    }
  };

  const openEdit = (rule) => {
    setEditingRule(rule);
    setFormData({
      match_type: rule.match_type,
      match_id: rule.match_id || "",
      to_emails: rule.to_emails.join(", "),
      cc_emails: rule.cc_emails.join(", "),
      enabled: rule.enabled,
      event_types: rule.event_types
    });
    setSheetOpen(true);
  };

  const getMatchOptions = () => {
    switch (formData.match_type) {
      case "ITEM": return items.map(i => ({ id: i.id, name: `${i.name} (${i.sku})` }));
      case "LOCATION": return locations.map(l => ({ id: l.id, name: l.name }));
      case "DEPARTMENT": return departments.map(d => ({ id: d.id, name: d.name }));
      default: return [];
    }
  };

  const getMatchName = (rule) => {
    if (rule.match_type === "DEFAULT") return "All Items";
    
    let list;
    switch (rule.match_type) {
      case "ITEM": list = items; break;
      case "LOCATION": list = locations; break;
      case "DEPARTMENT": list = departments; break;
      default: return "-";
    }
    const match = list.find(x => x.id === rule.match_id);
    return match?.name || rule.match_id;
  };

  return (
    <div className="flex-1 flex flex-col">
      {/* Header */}
      <div className="h-16 border-b border-border px-6 flex items-center justify-between bg-card">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 border border-primary/30 rounded-sm flex items-center justify-center">
            <Bell className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="font-heading text-2xl tracking-tight">NOTIFICATIONS</h1>
            <p className="text-xs text-muted-foreground">Configure rules and view delivery log</p>
          </div>
        </div>
      </div>
      
      {/* Content with Tabs */}
      <Tabs defaultValue="rules" className="flex-1 flex flex-col">
        <div className="border-b border-border px-6 bg-card/50">
          <TabsList className="bg-transparent h-12">
            <TabsTrigger value="rules" className="uppercase tracking-wider data-[state=active]:bg-primary/10">
              Rules
            </TabsTrigger>
            <TabsTrigger value="delivery" className="uppercase tracking-wider data-[state=active]:bg-primary/10">
              Delivery Log
              {deliveryStats?.by_status?.FAILED > 0 && (
                <Badge className="ml-2 bg-red-900/30 text-red-400 text-xs">{deliveryStats.by_status.FAILED}</Badge>
              )}
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Rules Tab */}
        <TabsContent value="rules" className="flex-1 m-0">
          <div className="p-4 border-b border-border bg-card/30 flex justify-end">
            <Button onClick={() => { resetForm(); setSheetOpen(true); }} className="rounded-sm uppercase tracking-wider" data-testid="add-rule-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Rule
            </Button>
          </div>
          <ScrollArea className="flex-1">
            <div className="p-6">
              <div className="industrial-card overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="industrial-table-header">
                      <TableHead>MATCH TYPE</TableHead>
                      <TableHead>MATCH</TableHead>
                      <TableHead>TO EMAILS</TableHead>
                      <TableHead>EVENTS</TableHead>
                      <TableHead>STATUS</TableHead>
                      <TableHead className="w-24">ACTIONS</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          Loading...
                        </TableCell>
                      </TableRow>
                    ) : rules.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          No notification rules configured
                        </TableCell>
                      </TableRow>
                    ) : rules.map((rule) => (
                      <TableRow key={rule.id} className="industrial-table-row" data-testid={`rule-row-${rule.id}`}>
                        <TableCell>
                          <Badge className="industrial-badge bg-muted text-foreground border-border">
                            {rule.match_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">{getMatchName(rule)}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {rule.to_emails.join(", ") || "-"}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {rule.event_types.map(evt => (
                              <Badge key={evt} variant="outline" className="text-xs">
                                {EVENT_LABEL[evt] || evt}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`industrial-badge ${rule.enabled ? "bg-green-900/30 text-green-400 border-green-900" : "bg-muted text-muted-foreground border-border"}`}>
                            {rule.enabled ? "ENABLED" : "DISABLED"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEdit(rule)}
                              className="h-8 w-8"
                              data-testid={`edit-rule-${rule.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(rule.id)}
                              className="h-8 w-8 text-destructive"
                              data-testid={`delete-rule-${rule.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </ScrollArea>
        </TabsContent>

        {/* Delivery Log Tab */}
        <TabsContent value="delivery" className="flex-1 m-0">
          <div className="p-4 border-b border-border bg-card/30 flex items-center justify-between">
            {/* Stats */}
            <div className="flex items-center gap-4 text-sm">
              {deliveryStats && (
                <>
                  <span className="text-muted-foreground">Total: <span className="text-foreground font-mono">{deliveryStats.total}</span></span>
                  <span className="text-green-400">Sent: <span className="font-mono">{deliveryStats.by_status?.SENT || 0}</span></span>
                  <span className="text-red-400">Failed: <span className="font-mono">{deliveryStats.by_status?.FAILED || 0}</span></span>
                  <span className="text-yellow-400">Pending: <span className="font-mono">{deliveryStats.by_status?.PENDING || 0}</span></span>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={fetchDeliveryLog} className="rounded-sm" data-testid="refresh-delivery-log">
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingDelivery ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setClearDialogOpen(true)} 
                className="rounded-sm text-destructive hover:text-destructive"
                data-testid="clear-logs-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear Logs
              </Button>
              <Button onClick={() => setTestDialogOpen(true)} className="rounded-sm uppercase tracking-wider" data-testid="send-test-btn">
                <Mail className="w-4 h-4 mr-2" />
                Send Test
              </Button>
            </div>
          </div>
          <ScrollArea className="flex-1">
            <div className="p-6">
              <div className="industrial-card overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="industrial-table-header">
                      <TableHead>TIME</TableHead>
                      <TableHead>EVENT</TableHead>
                      <TableHead>ITEM</TableHead>
                      <TableHead>RECIPIENTS</TableHead>
                      <TableHead>STATUS</TableHead>
                      <TableHead>ERROR</TableHead>
                      <TableHead className="w-20">ACTIONS</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loadingDelivery ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          Loading...
                        </TableCell>
                      </TableRow>
                    ) : deliveryLog.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          No notification history
                        </TableCell>
                      </TableRow>
                    ) : deliveryLog.map((entry) => (
                      <TableRow key={entry.id} className="industrial-table-row" data-testid={`delivery-row-${entry.id}`}>
                        <TableCell className="text-muted-foreground text-xs font-mono">
                          {new Date(entry.created_at).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {EVENT_LABEL[entry.event_type] || entry.event_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium text-sm">
                          {entry.item_name || "-"}
                          {entry.item_sku && <span className="text-muted-foreground text-xs ml-1">({entry.item_sku})</span>}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-xs max-w-40 truncate">
                          {entry.recipients?.join(", ") || "-"}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(entry.status, entry.sent_via)}
                        </TableCell>
                        <TableCell className="text-xs max-w-48">
                          {entry.last_error_code ? (
                            <div>
                              <span className="text-red-400 font-mono">{entry.last_error_code}</span>
                              {entry.last_error_message && (
                                <p className="text-muted-foreground truncate mt-0.5" title={String(entry.last_error_message)}>
                                  {String(entry.last_error_message)}
                                </p>
                              )}
                            </div>
                          ) : entry.last_error ? (
                            <span className="text-red-400 truncate" title={normalizeError(entry.last_error)}>{normalizeError(entry.last_error)}</span>
                          ) : "-"}
                        </TableCell>
                        <TableCell>
                          {entry.status === "FAILED" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleResend(entry.id)}
                              disabled={resending === entry.id}
                              className="h-7 px-2 text-xs"
                              data-testid={`resend-${entry.id}`}
                            >
                              {resending === entry.id ? (
                                <RefreshCw className="w-3 h-3 animate-spin" />
                              ) : (
                                <>
                                  <Send className="w-3 h-3 mr-1" />
                                  Resend
                                </>
                              )}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </ScrollArea>
        </TabsContent>
      </Tabs>
      
      {/* Edit Sheet */}
      <Sheet open={sheetOpen} onOpenChange={(open) => { setSheetOpen(open); if (!open) resetForm(); }}>
        <SheetContent className="w-[450px] sm:max-w-[450px] bg-card border-l border-border overflow-y-auto">
          <SheetHeader className="mb-6">
            <SheetTitle className="font-heading text-xl tracking-tight">
              {editingRule ? "EDIT RULE" : "NEW RULE"}
            </SheetTitle>
            <SheetDescription>
              Configure when and to whom notifications are sent
            </SheetDescription>
          </SheetHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label className="text-xs text-muted-foreground uppercase">Match Type</label>
              <Select value={formData.match_type} onValueChange={(v) => setFormData({ ...formData, match_type: v, match_id: "" })}>
                <SelectTrigger className="industrial-input" data-testid="rule-match-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MATCH_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {formData.match_type !== "DEFAULT" && (
              <div className="space-y-2">
                <label className="text-xs text-muted-foreground uppercase">Match {formData.match_type}</label>
                <Select value={formData.match_id} onValueChange={(v) => setFormData({ ...formData, match_id: v })}>
                  <SelectTrigger className="industrial-input" data-testid="rule-match-id">
                    <SelectValue placeholder={`Select ${formData.match_type.toLowerCase()}`} />
                  </SelectTrigger>
                  <SelectContent>
                    {getMatchOptions().map(opt => (
                      <SelectItem key={opt.id} value={opt.id}>{opt.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="space-y-2">
              <label className="text-xs text-muted-foreground uppercase">To Emails (comma-separated)</label>
              <Input
                value={formData.to_emails}
                onChange={(e) => setFormData({ ...formData, to_emails: e.target.value })}
                placeholder="email1@company.com, email2@company.com"
                className="industrial-input"
                data-testid="rule-to-emails"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-xs text-muted-foreground uppercase">CC Emails (comma-separated)</label>
              <Input
                value={formData.cc_emails}
                onChange={(e) => setFormData({ ...formData, cc_emails: e.target.value })}
                placeholder="manager@company.com"
                className="industrial-input"
                data-testid="rule-cc-emails"
              />
            </div>
            
            <div className="space-y-3">
              <label className="text-xs text-muted-foreground uppercase">Event Types</label>
              <div className="space-y-2">
                {EVENT_META.filter(e => e.code !== "TEST").map(evt => (
                  <div key={evt.code} className="flex items-start gap-3 py-1">
                    <Checkbox
                      id={evt.code}
                      checked={formData.event_types.includes(evt.code)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setFormData({ ...formData, event_types: [...formData.event_types, evt.code] });
                        } else {
                          setFormData({ ...formData, event_types: formData.event_types.filter(e => e !== evt.code) });
                        }
                      }}
                      className="mt-0.5"
                      data-testid={`rule-event-${evt.code}`}
                    />
                    <div>
                      <label htmlFor={evt.code} className="text-sm font-medium cursor-pointer">{evt.label}</label>
                      <p className="text-xs text-muted-foreground">{evt.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-sm">
              <label className="text-sm">Rule Enabled</label>
              <Switch
                checked={formData.enabled}
                onCheckedChange={(v) => setFormData({ ...formData, enabled: v })}
                data-testid="rule-enabled"
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1 uppercase tracking-wider rounded-sm" data-testid="save-rule-btn">
                {editingRule ? "Update Rule" : "Create Rule"}
              </Button>
              <Button type="button" variant="secondary" onClick={() => setSheetOpen(false)} className="uppercase tracking-wider rounded-sm">
                Cancel
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Test Email Dialog */}
      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl tracking-tight flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" />
              SEND TEST NOTIFICATION
            </DialogTitle>
            <DialogDescription>
              Send a test email to verify your Gmail integration is working.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-xs text-muted-foreground uppercase">Recipient Email</label>
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="test@example.com"
                className="industrial-input"
                data-testid="test-email-input"
              />
            </div>
            <div className="p-3 bg-muted/30 rounded-sm text-sm text-muted-foreground">
              <p>This will send a test email using your configured Gmail settings.</p>
              <p className="mt-1">If Gmail is not configured, the email will be logged as MOCK.</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="secondary" onClick={() => setTestDialogOpen(false)} className="uppercase tracking-wider rounded-sm">
              Cancel
            </Button>
            <Button 
              onClick={handleSendTest} 
              disabled={sendingTest || !testEmail}
              className="uppercase tracking-wider rounded-sm"
              data-testid="confirm-send-test"
            >
              {sendingTest ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Test
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear Logs Dialog */}
      <Dialog open={clearDialogOpen} onOpenChange={(open) => {
        setClearDialogOpen(open);
        if (!open) {
          setClearScope("all");
          setClearOlderThan("");
          setClearConfirmText("");
        }
      }}>
        <DialogContent className="bg-card border-border sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl tracking-tight flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-destructive" />
              CLEAR NOTIFICATION LOGS
            </DialogTitle>
            <DialogDescription>
              This will permanently delete delivery log entries. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Scope Selection */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground uppercase">Scope</Label>
              <Select value={clearScope} onValueChange={setClearScope}>
                <SelectTrigger className="industrial-input" data-testid="clear-scope-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Logs</SelectItem>
                  <SelectItem value="sent">Only SENT</SelectItem>
                  <SelectItem value="failed">Only FAILED</SelectItem>
                  <SelectItem value="pending">Only PENDING</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Age Filter */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground uppercase">Older Than (Days)</Label>
              <Select value={clearOlderThan || "none"} onValueChange={(v) => setClearOlderThan(v === "none" ? "" : v)}>
                <SelectTrigger className="industrial-input" data-testid="clear-age-select">
                  <SelectValue placeholder="No age filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No age filter (all dates)</SelectItem>
                  <SelectItem value="7">Older than 7 days</SelectItem>
                  <SelectItem value="30">Older than 30 days</SelectItem>
                  <SelectItem value="90">Older than 90 days</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Leave empty to delete logs of any age
              </p>
            </div>

            {/* Current selection summary */}
            <div className="p-3 bg-muted/30 rounded-sm text-sm border border-border">
              <p className="font-medium mb-1">You are about to delete:</p>
              <p className="text-muted-foreground">
                {clearScope === "all" ? "All" : clearScope.toUpperCase()} logs
                {clearOlderThan ? ` older than ${clearOlderThan} days` : " (any age)"}
              </p>
            </div>
            
            {/* Confirmation for ALL without age filter */}
            {clearScope === "all" && !clearOlderThan && (
              <div className="space-y-2 p-3 bg-destructive/10 rounded-sm border border-destructive/30">
                <p className="text-sm text-destructive font-medium flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Deleting ALL logs requires confirmation
                </p>
                <Label className="text-xs text-muted-foreground uppercase">Type CLEAR to confirm</Label>
                <Input
                  value={clearConfirmText}
                  onChange={(e) => setClearConfirmText(e.target.value.toUpperCase())}
                  placeholder="Type CLEAR"
                  className="industrial-input font-mono"
                  data-testid="clear-confirm-input"
                />
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="secondary" 
              onClick={() => setClearDialogOpen(false)} 
              className="uppercase tracking-wider rounded-sm"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleClearLogs}
              disabled={clearing || !canClear()}
              variant="destructive"
              className="uppercase tracking-wider rounded-sm"
              data-testid="confirm-clear-logs"
            >
              {clearing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Clearing...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear Logs
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
